<template>
  <div class="flex min-h-0 flex-1 flex-col bg-surface-50 dark:bg-surface-950">
    <!-- Mobile sidebar -->
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-50 lg:hidden" @close="closeSidebar">
        <!-- overlay that covers the screen when the sidebar is open -->
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-surface-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <!-- sidebar/nav thing that slides in from the left -->
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel
              class="relative mr-16 flex flex-col bg-surface-0 dark:bg-surface-900 w-full max-w-xs flex-1"
            >
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="flex justify-between items-center py-4 px-8 w-full">
                  <img
                    :src="sidebarLogoSrc"
                    alt="Reach Logo"
                    class="h-6 w-auto"
                  />
                  <button
                    type="button"
                    class="-m-2.5 p-2.5 rounded-md text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 focus:outline-none focus:ring-2 focus:ring-rch-black"
                    @click="closeSidebar"
                  >
                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>
              <div
                class="flex grow flex-col gap-y-5 overflow-y-auto bg-surface-0 dark:bg-surface-900 px-6 pb-4 h-full"
              >
                <SidebarNavigation
                  :navigation="filteredNavigation"
                  :collapsed="false"
                  :is-mobile="true"
                  @close-sidebar="closeSidebar"
                  @search-click="handleSearchClick"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div
      class="hidden lg:flex lg:flex-col lg:max-w-[17rem] relative group h-full"
    >
      <!-- Hide button on hover -->
      <div
        class="absolute -right-3 top-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
      >
        <button
          type="button"
          class="bg-surface-0 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-full p-1.5 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 hover:bg-surface-50 dark:hover:bg-surface-800"
          @click="toggleSidebarCollapsed"
          data-test-id="sidebar-toggle"
        >
          <ChevronLeftIcon
            v-if="!collapsed"
            class="h-4 w-4 text-surface-600 dark:text-surface-300"
          />
          <ChevronRightIcon
            v-else
            class="h-4 w-4 text-surface-600 dark:text-surface-300"
          />
        </button>
      </div>

      <div
        :class="[
          'flex grow flex-col gap-y-5 overflow-y-auto border-r border-surface-200 dark:border-surface-700 bg-surface-0 dark:bg-surface-900 px-6 py-4 transition-all duration-300',
          collapsed ? 'lg:w-16' : 'lg:w-[17rem]',
        ]"
      >
        <SidebarNavigation
          :navigation="filteredNavigation"
          :collapsed="collapsed"
          :is-mobile="false"
          @close-sidebar="closeSidebar"
          @search-click="handleSearchClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useFrontegg } from '@frontegg/vue'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'
import logoBlack from '@/assets/Reach-Logo-Black.svg'
import logoWhite from '@/assets/Reach-Logo-White.svg'
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'
import { useSidebar } from '@/composables/useSidebar'
import { useSearchStore } from '@/stores/searchStore'
import { useThemeStore } from '@/stores/themeStore'
import SidebarNavigation from './SidebarNavigation.vue'

const {
  isSidebarOpen: sidebarOpen,
  isSidebarCollapsed: collapsed,
  closeSidebar,
  toggleSidebarCollapsed,
} = useSidebar()
const { hasPerms } = usePerms()

const searchStore = useSearchStore()
const route = useRoute()
const router = useRouter()
const { authState } = useFrontegg()
const themeStore = useThemeStore()
const { isDark } = storeToRefs(themeStore)

const sidebarLogoSrc = computed(() => {
  return isDark.value ? logoWhite : logoBlack
})

// Build navigation from router routes
const buildNavigationFromRouter = () => {
  const sections = new Map()

  router.options.routes.forEach(routerRoute => {
    if (routerRoute.meta?.section && routerRoute.meta?.displayName) {
      // Handle main section routes
      if (!routerRoute.children) {
        const sectionName = routerRoute.meta.section.toUpperCase()
        if (!sections.has(sectionName)) {
          sections.set(sectionName, {
            name: sectionName,
            showHeader: false,
            items: [],
          })
        }

        // Check permissions
        const perm = routerRoute.meta.perm
        if (!perm || hasPerms(perm)) {
          sections.get(sectionName).items.push({
            name: routerRoute.meta.displayName,
            routeName: routerRoute.name,
            icon: routerRoute.meta.icon,
            perm: perm,
          })
        }
      } else {
        // Handle routes with children
        const sectionName = routerRoute.meta.section.toUpperCase()
        if (!sections.has(sectionName)) {
          sections.set(sectionName, {
            name: sectionName,
            showHeader: true,
            items: [],
          })
        }

        // If the parent route has showInSidebar: true, add it as a main item
        if (routerRoute.meta?.showInSidebar) {
          const perm = routerRoute.meta.perm
          if (!perm || hasPerms(perm)) {
            sections.get(sectionName).items.push({
              name: routerRoute.meta.displayName,
              routeName: routerRoute.name,
              icon: routerRoute.meta.icon,
              perm: perm,
            })
          }
        }

        // Add children routes
        routerRoute.children.forEach(child => {
          const perm = child.meta?.perm
          if ((!perm || hasPerms(perm)) && child.meta?.showInSidebar) {
            // Check if this child route has its own children
            if (child.children && child.children.length > 0) {
              // Add the child route as a main item if it has showInSidebar: true
              if (child.meta?.showInSidebar) {
                sections.get(sectionName).items.push({
                  name: child.meta.displayName,
                  routeName: child.name,
                  icon: child.meta.icon,
                  perm: perm,
                })
              }

              // Process the child's children
              child.children.forEach(grandChild => {
                const grandChildPerm = grandChild.meta?.perm
                if (
                  (!grandChildPerm || hasPerms(grandChildPerm)) &&
                  grandChild.meta?.showInSidebar
                ) {
                  sections.get(sectionName).items.push({
                    name: grandChild.meta.displayName,
                    routeName: grandChild.name,
                    icon: grandChild.meta.icon,
                    perm: grandChildPerm,
                    isSubItem: true,
                  })
                }

                // Show merchant config tab routes when a merchant is selected
                if (
                  grandChild.children?.length &&
                  route.params.id &&
                  route.matched.some(r => r.name === grandChild.name)
                ) {
                  grandChild.children.forEach(greatGrandChild => {
                    const ggPerm = greatGrandChild.meta?.perm
                    if (
                      (!ggPerm || hasPerms(ggPerm)) &&
                      greatGrandChild.meta?.showInSidebar
                    ) {
                      sections.get(sectionName).items.push({
                        name: greatGrandChild.meta.displayName,
                        routeName: greatGrandChild.name,
                        icon: greatGrandChild.meta.icon,
                        perm: ggPerm,
                        isSubItem: true,
                        params: { id: route.params.id },
                      })
                    }
                  })
                }
              })
            } else {
              // Regular child route handling (no grandchildren)
              sections.get(sectionName).items.push({
                name: child.meta.displayName,
                routeName: child.name,
                icon: child.meta.icon,
                perm: perm,
              })
            }
          }
        })
      }
    }
  })

  // Add tax transaction search button to TAX section
  if (sections.has('TAX') && hasPerms(PERMISSIONS.TAX_TRANSACTION_READ)) {
    const taxSection = sections.get('TAX')
    // Add search button as the last item in TAX section
    taxSection.items.push({
      name: 'Tax Transaction Search',
      routeName: 'taxTransactionSearch',
      icon: MagnifyingGlassIcon,
      perm: PERMISSIONS.TAX_TRANSACTION_READ,
      isSearchButton: true,
    })
  }

  return Array.from(sections.values()).filter(
    section => section.items.length > 0,
  )
}

const filteredNavigation = computed(() => {
  if (!authState.user || !authState.isAuthenticated) {
    return []
  }

  return buildNavigationFromRouter()
})

const handleSearchClick = () => {
  closeSidebar()
  searchStore.openSearchDialog()
}
</script>
